<template>
    <div id="payment-type-options">
        <button @click="createTransaction" class="button-secondary">
            <div id="googlepay-mark" class="sprite google-pay-light" />
        </button>
    </div>
</template>

<script>
    import axios from "axios";
    import braintree from "braintree-web";
    import { mapGetters, mapMutations, mapState } from "vuex";
    import { sweetAlert2 } from "../../../mixins/sweet-alert2";
    import { showBlocker } from "../../../mixins/show-blocker";
    import { paymentMethods } from "../../../constants/payment-methods";
    import { appSettings } from "../../../mixins/app-settings";

    export default {
        name: "GooglePay",
        mixins: [sweetAlert2, showBlocker, appSettings],
        data() {
            return {
                isGooglePayInitialized: false,
                isGooglePayAvailable: false
            }
        },
        computed: {
            ...mapState({
                siteId: state => state.session.siteId,
                accountId: state => state.session.accountId,
                checkId: state => state.session.checkId,
                tip: state => state.session.tip,
                googlePayScriptLoaded: state => state.session.googlePayScriptLoaded,
                brainTreeGooglePayComponent: state => state.session.brainTreeGooglePayComponent,
                googlePaymentClient: state => state.session.googlePaymentClient,
                isLoading: state => state.session.showBlocker
            }),
            ...mapGetters([
                "balanceWithTip"
            ])
        },
        methods: {
            ...mapMutations([
                "setLoadingStatus",
                "setPayment",
                "setGooglePayScriptLoaded",
                "setBrainTreeGooglePayComponent",
                "setGooglePaymentClient"
            ]),
            async loadGooglePayScriptAndInitializeClient() {
                if (!this.googlePayScriptLoaded) {
                    this.$loadScript("https://pay.google.com/gp/p/js/pay.js")
                        .then(async () => {
                            this.setGooglePayScriptLoaded(true);
                            await this.createGooglePayClient();
                        })
                        .catch(() => {
                            // If we have trouble loading the Google Pay script, we disable this
                            // form of payment.
                            this.isGooglePayInitialized = true;
                            this.isGooglePayAvailable = false;
                            console.error("Unable to load Google Pay script.");
                        })
                }
                else {
                    await this.createGooglePayClient();
                }
            },
            async createGooglePayClient() {
                // Get Google Pay environment
                let googlePayEnvironment = await axios.get(this.getApiBaseUrl() + "/payment/googlePayEnvironment/" + this.siteId + "/" + this.accountId)
                    .then(response => { return response.data })
                    .catch(error => {
                        console.error(error);
                        this.isGooglePayInitialized = true;
                    })

                if (googlePayEnvironment !== "TEST" && googlePayEnvironment !== "PRODUCTION") {
                    console.error("Invalid Google Pay Environment: " + googlePayEnvironment);
                    this.$router.push({ name: "Error", params: { errorCode: "1021" } });
                    return;
                }

                // Initialize Google payment client.
                this.setGooglePaymentClient(new window.google.payments.api.PaymentsClient({
                    environment: googlePayEnvironment
                }));

                let hasExited = false;

                // Generate clientToken.
                const url = this.getApiBaseUrl() + "/payment/clientToken/" + this.siteId + "/" + this.accountId;

                let clientToken = await axios.get(url)
                    .then(response => {
                        if (response.data) {
                            return response.data
                        }
                        else {
                            this.$router.push({ name: "Error", params: { errorCode: "1012" } });
                            hasExited = true;
                        }
                    })
                    .catch(error => {
                        if (error.response &&
                            error.response.data &&
                            error.response.data.errorCode) {
                            this.$router.push({ name: "Error", params: { errorCode: error.response.data.errorCode } });
                            hasExited = true;
                        }
                        else {
                            console.error(error);
                            this.isGooglePayInitialized = true;
                        }
                    })

                if (hasExited) {
                    return;
                }

                // Create the Braintree Google Payment component.
                if (clientToken !== undefined) {
                    await braintree.client.create({
                        authorization: clientToken
                    })
                        .then(clientInstance => {
                            return braintree.googlePayment.create({
                                client: clientInstance,
                                googlePayVersion: 2
                            });
                        })
                        .then(async (result) => {
                            this.setBrainTreeGooglePayComponent(result);
                            await this.checkIfReadyToPay();
                        })
                        .finally(() => {
                            this.isGooglePayInitialized = true;
                        });
                }
            },
            async checkIfReadyToPay() {
                this.googlePaymentClient.isReadyToPay({
                    apiVersion: 2,
                    apiVersionMinor: 0,
                    allowedPaymentMethods: this.brainTreeGooglePayComponent
                        .createPaymentDataRequest().allowedPaymentMethods
                })
                    .then(response => {
                        if (response.result) {
                            this.isGooglePayAvailable = true;
                        }
                    })
                    .catch(error => {
                        console.error("Error determining readiness to use Google Pay: ", error);
                    })
                    .finally(() => {
                        this.isGooglePayInitialized = true;
                    });
            },
            async createTransaction() {
                // In the rare event that user triggers Google Pay clicked event before the
                // initialization is done, we have to ignore the event. User would have the
                // clue to tap the button again, and usually by then, the initialization
                // would be done. We cannot auto trigger a call to this function at a later
                // time with something like a setTimeout, because the pop up blocker would
                // not allow that.
                if (!this.isGooglePayInitialized) {
                    return;
                }

                // If Google Pay is not supported for some reason, we will ask user to try another
                // payment method.
                if (!this.isGooglePayAvailable) {
                    console.error("Google Pay failed to initialize but Google Pay payment triggered.");
                    this.swalError("1015");
                    return;
                }

                if (!this.isLoading) {
                    this.showBlockerWithNoSpinner();
                }

                const transactionInfo = {
                    totalPriceStatus: 'FINAL',
                    totalPrice: this.balanceWithTip.toFixed(2).toString(),
                    currencyCode: 'USD'
                };

                let paymentDataRequest = this.brainTreeGooglePayComponent.createPaymentDataRequest({
                    transactionInfo: transactionInfo
                });

                await this.processPayment(paymentDataRequest);
            },
            async processPayment(paymentDataRequest) {
                this.googlePaymentClient.loadPaymentData(paymentDataRequest)
                    .then(paymentData => { return this.brainTreeGooglePayComponent.parseResponse(paymentData); })
                    .then(async (result) => {
                        await this.sendPayloadToServer(result);
                    })
                    .catch(error => {
                        this.stopProgressIndicator();
                        console.error(error);

                        // A "cancel" is considered an error. For anything other than a "cancel", it's
                        // an unknown error condition that we have to handle.
                        if (error !== undefined && error.statusCode !== "CANCELED") {
                            this.$router.push({ name: "Error", params: { errorCode: "1016" } });
                        }
                    });
            },
            async sendPayloadToServer(payment) {
                // Google Pay is done at this point (pop up closed). We need to show the spinner.
                this.startProgressIndicator();

                let payload = {
                    SiteId: this.siteId,
                    AccountId: this.accountId,
                    CheckId: this.checkId,
                    PaymentAmount: this.balanceWithTip,
                    TipAmount: this.tip,
                    PaymentNonce: payment.nonce
                }

                await axios.post(this.getApiBaseUrl() + "/payment/payment", payload)
                    .then(response => {
                        if (response.data) {
                            let payment = response.data;
                            payment.paymentName = paymentMethods.googlePay;
                            this.setPayment(payment);
                            this.$router.push({ name: "PaymentSummary" });
                        }
                        else {
                            this.$router.push({ name: "Error", params: { errorCode: "1000" } });
                        }
                    })
                    .catch(error => {
                        if (error.response &&
                            error.response.data &&
                            error.response.data.errorCode) {
                            switch (error.response.data.errorCode) {
                                case "1008":
                                case "1009":
                                    this.$router.push({ name: "Error", params: { errorCode: error.response.data.errorCode } });
                                    break;
                                default:
                                    if (error.response.data.errorMessage) {
                                        console.error(error.response.data.errorMessage);
                                    }
                                    this.$router.push({ name: "Error", params: { errorCode: "1010" } });
                            }
                        }
                        else {
                            console.error(error);
                            this.$router.push({ name: "Error", params: { errorCode: "1010" } });
                        }
                    })
                    .finally(() => {
                        this.stopProgressIndicator();
                    });
            }
        },
        async mounted() {
            // Google Pay has been initialized previously. Simply reuse those objects from Vuex.
            if (this.googlePaymentClient !== null && this.brainTreeGooglePayComponent !== null) {
                this.checkIfReadyToPay();
                return;
            }

            // Pop up will get blocked if the event handler has any callback or AJAX call in Safari.
            // We need to initialize Google Pay even if user is on an Apple device as those AJAX calls
            // during initialization would prevent the pop up Google Pay sheet from opening.
            await this.loadGooglePayScriptAndInitializeClient();
        }
    }
</script>